/**
 * Customer Setup / Self-Service Onboarding
 *
 * Shown when a customer IT admin signs in for the first time
 * (GET /customer/me returns 404). Walks them through 3 steps:
 *   1. Organisation details
 *   2. IT policy (what AI tools they have / support)
 *   3. AI strategy context (optional free text)
 *
 * On completion calls POST /customer/onboard and navigates to the dashboard.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Card,
  Text,
  Input,
  Textarea,
  Select,
  Checkbox,
  makeStyles,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { useApiClient, customerService } from '../../services/apiService';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '48px 24px',
  },
  card: { width: '100%', maxWidth: '600px', padding: '32px' },
  title: { fontSize: '24px', fontWeight: '700', marginBottom: '8px' },
  subtitle: { fontSize: '14px', color: tokens.colorNeutralForeground3, marginBottom: '32px' },
  steps: { display: 'flex', gap: '8px', marginBottom: '32px' },
  step: {
    flex: 1,
    height: '4px',
    borderRadius: '2px',
    backgroundColor: tokens.colorNeutralStroke1,
  },
  stepActive: { backgroundColor: tokens.colorBrandBackground },
  form: { display: 'flex', flexDirection: 'column', gap: '20px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '14px', fontWeight: '500' },
  hint: { fontSize: '12px', color: tokens.colorNeutralForeground3 },
  row: { display: 'flex', gap: '16px' },
  checkboxGroup: { display: 'flex', flexDirection: 'column', gap: '10px' },
  actions: { display: 'flex', justifyContent: 'space-between', marginTop: '8px' },
  error: { color: tokens.colorStatusDangerForeground1, fontSize: '13px' },
});

const INDUSTRIES = [
  'Financial Services', 'Healthcare', 'Legal & Professional Services',
  'Technology', 'Retail & Consumer', 'Manufacturing', 'Education',
  'Government & Public Sector', 'Media & Communications', 'Other',
];

const ORG_SIZES = ['1–50', '51–200', '201–1,000', '1,001–5,000', '5,000+'];

const COUNTRIES = [
  'United Kingdom', 'United States', 'Australia', 'New Zealand', 'Canada',
  'Germany', 'France', 'Netherlands', 'Sweden', 'Singapore', 'Other',
];

interface FormData {
  company_name: string;
  domain: string;
  org_country: string;
  org_industry: string;
  org_size_bracket: string;
  supports_m365_copilot: boolean;
  supports_copilot_studio: boolean;
  supports_azure_ai: boolean;
  org_notes: string;
  org_context: string;
}

export default function CustomerSetup({ onComplete }: { onComplete?: () => void }) {
  const styles = useStyles();
  const navigate = useNavigate();
  const { accounts } = useMsal();
  useApiClient(); // Attaches auth interceptor

  // Extract domain from the signed-in user's UPN (e.g. dan@shapeforscale.ai → shapeforscale.ai)
  const userDomain = accounts[0]?.username?.split('@')[1] || '';

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [form, setForm] = useState<FormData>({
    company_name: '',
    domain: userDomain,
    org_country: '',
    org_industry: '',
    org_size_bracket: '',
    supports_m365_copilot: false,
    supports_copilot_studio: false,
    supports_azure_ai: false,
    org_notes: '',
    org_context: '',
  });

  const set = (field: keyof FormData, value: any) =>
    setForm((f) => ({ ...f, [field]: value }));

  const handleNext = () => {
    setError(null);
    if (step === 1) {
      if (!form.company_name.trim()) { setError('Company name is required'); return; }
      if (!form.org_country) { setError('Country is required'); return; }
      if (!form.org_industry) { setError('Industry is required'); return; }
      if (!form.org_size_bracket) { setError('Organisation size is required'); return; }
    }
    setStep((s) => s + 1);
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      await customerService.onboard({
        company_name: form.company_name.trim(),
        domain: form.domain.trim() || undefined,
        org_country: form.org_country,
        industry: form.org_industry,
        org_size_bracket: form.org_size_bracket,
        it_policy: {
          supports_m365_copilot: form.supports_m365_copilot,
          supports_copilot_studio: form.supports_copilot_studio,
          supports_azure_ai: form.supports_azure_ai,
          org_notes: form.org_notes.trim() || null,
        },
        org_context: form.org_context.trim() || undefined,
        admin_name: accounts[0]?.name || undefined,
        admin_email: accounts[0]?.username || undefined,
      });
      if (onComplete) { onComplete(); } else { navigate('/customer/dashboard'); }
    } catch (err: any) {
      if (err?.response?.status === 409) {
        // Already onboarded — just go to dashboard
        if (onComplete) { onComplete(); } else { navigate('/customer/dashboard'); }
      } else {
        setError(err?.response?.data?.error || 'Setup failed. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.page}>
      <Card className={styles.card}>
        <Text className={styles.title}>Welcome to Sherpai</Text>
        <Text className={styles.subtitle}>
          Let's set up your organisation so Sherpai can give your team the most relevant answers.
          This takes about 2 minutes.
        </Text>

        {/* Progress bar */}
        <div className={styles.steps}>
          {[1, 2, 3].map((s) => (
            <div key={s} className={`${styles.step} ${s <= step ? styles.stepActive : ''}`} />
          ))}
        </div>

        {step === 1 && (
          <div className={styles.form}>
            <Text weight="semibold">Step 1 of 3 — Organisation details</Text>

            <div className={styles.field}>
              <Text className={styles.label}>Company name *</Text>
              <Input
                value={form.company_name}
                onChange={(_, d) => set('company_name', d.value)}
                placeholder="Contoso Ltd"
              />
            </div>

            <div className={styles.field}>
              <Text className={styles.label}>Email domain</Text>
              <Input
                value={form.domain}
                onChange={(_, d) => set('domain', d.value)}
                placeholder="contoso.com"
              />
              <Text className={styles.hint}>Optional — helps Sherpai identify your organisation</Text>
            </div>

            <div className={styles.row}>
              <div className={styles.field} style={{ flex: 1 }}>
                <Text className={styles.label}>Country *</Text>
                <Select value={form.org_country} onChange={(_, d) => set('org_country', d.value)}>
                  <option value="">Select country</option>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </Select>
              </div>
              <div className={styles.field} style={{ flex: 1 }}>
                <Text className={styles.label}>Organisation size *</Text>
                <Select value={form.org_size_bracket} onChange={(_, d) => set('org_size_bracket', d.value)}>
                  <option value="">Select size</option>
                  {ORG_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
            </div>

            <div className={styles.field}>
              <Text className={styles.label}>Industry *</Text>
              <Select value={form.org_industry} onChange={(_, d) => set('org_industry', d.value)}>
                <option value="">Select industry</option>
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className={styles.form}>
            <Text weight="semibold">Step 2 of 3 — Microsoft AI tools</Text>
            <Text className={styles.hint}>
              Tell us which Microsoft AI tools your organisation supports. Sherpai uses this to give
              your users answers based on what they can actually use — not just what exists.
            </Text>

            <div className={styles.checkboxGroup}>
              <Checkbox
                label="Microsoft 365 Copilot (paid licence — in Word, Excel, Teams, Outlook)"
                checked={form.supports_m365_copilot}
                onChange={(_, d) => set('supports_m365_copilot', d.checked)}
              />
              <Checkbox
                label="Copilot Studio (custom agent / chatbot builder)"
                checked={form.supports_copilot_studio}
                onChange={(_, d) => set('supports_copilot_studio', d.checked)}
              />
              <Checkbox
                label="Azure AI / Azure OpenAI (custom application development)"
                checked={form.supports_azure_ai}
                onChange={(_, d) => set('supports_azure_ai', d.checked)}
              />
            </div>

            <div className={styles.field}>
              <Text className={styles.label}>IT notes (optional)</Text>
              <Textarea
                value={form.org_notes}
                onChange={(_, d) => set('org_notes', d.value)}
                placeholder="e.g. All AI tools must be approved by the IT Security team before use. Data must remain within EU boundaries."
                rows={3}
              />
              <Text className={styles.hint}>Any policies or restrictions your users should know about</Text>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className={styles.form}>
            <Text weight="semibold">Step 3 of 3 — Your AI strategy (optional)</Text>
            <Text className={styles.hint}>
              If your organisation has a view on AI adoption — goals, priorities, approved use cases,
              or things to avoid — add it here. Sherpai will use this to frame every answer in the
              context of your strategy rather than giving generic guidance.
            </Text>

            <div className={styles.field}>
              <Text className={styles.label}>AI strategy and context</Text>
              <Textarea
                value={form.org_context}
                onChange={(_, d) => set('org_context', d.value)}
                placeholder="e.g. We are a FCA-regulated financial services firm. We are prioritising AI use cases that reduce manual document processing and improve client reporting. Staff are encouraged to use Copilot for productivity but must not input client data or PII into any AI tool without IT approval. We are evaluating Copilot Studio for an internal HR query bot."
                rows={6}
              />
              <Text className={styles.hint}>
                This is stored securely and only used to shape Sherpai's answers for your team.
                It is never shared externally.
              </Text>
            </div>
          </div>
        )}

        {error && <Text className={styles.error}>{error}</Text>}

        <div className={styles.actions}>
          {step > 1 ? (
            <Button onClick={() => setStep((s) => s - 1)} disabled={saving}>Back</Button>
          ) : (
            <span />
          )}
          {step < 3 ? (
            <Button appearance="primary" onClick={handleNext}>Next</Button>
          ) : (
            <Button appearance="primary" onClick={handleSubmit} disabled={saving}>
              {saving ? <Spinner size="tiny" /> : 'Complete setup'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
